import Layout from "./Layout";

export default function ShippingPolicy() {
  return (
    <Layout>
      <div className="bg-white text-black">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 py-12 lg:py-16">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-wide">
              Shipping Policy
            </h1>

            <div className="w-16 h-1 bg-hover_dark_primary mt-4 mb-6"></div>

            <p className="text-sm text-gray-600">
              <span className="font-semibold text-black">Effective Date:</span>{" "}
              August 31, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="space-y-5 text-[15px] sm:text-base leading-7 text-gray-700">
            <p>
              At <strong className="text-black">Legacy Decor</strong>, we take
              care in preparing and securely packing your order before handing
              it over to our delivery partners.
            </p>

            <p>
              This Shipping Policy explains how orders placed through{" "}
              <strong className="text-black">www.legacydecor.in</strong> are
              processed and delivered.
            </p>
          </div>

          {/* Policy Sections */}
          <div className="mt-10 space-y-9 text-[15px] sm:text-base leading-7 text-gray-700">
            {/* 1 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                1. Order Processing
              </h2>

              <p>
                Orders are generally processed after successful confirmation of
                payment.
              </p>

              <p className="mt-3">
                Orders may take approximately{" "}
                <strong className="text-black">1–3 business days</strong> to be
                processed and dispatched, unless a different timeline is
                specified on the product page.
              </p>

              <p className="mt-3">
                Orders placed on Sundays or public holidays may be processed on
                the next business day.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                2. Estimated Delivery Time
              </h2>

              <p>
                Once dispatched, delivery generally takes approximately{" "}
                <strong className="text-black">3–7 business days</strong>,
                depending on the delivery location and courier service.
              </p>

              <p className="mt-3">
                Remote or difficult-to-service locations may require additional
                delivery time.
              </p>

              <p className="mt-3">
                The delivery timeline displayed during checkout or communicated
                with the order shall prevail where specifically provided.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                3. Delivery Areas
              </h2>

              <p>
                We currently deliver to locations serviceable by our courier
                partners within India.
              </p>

              <p className="mt-3">
                Certain remote, restricted or non-serviceable locations may not
                be eligible for delivery.
              </p>

              <p className="mt-3">
                If delivery to a particular location is unavailable, we may
                contact the customer to arrange an alternative solution or
                cancel the order where necessary.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                4. Shipping Charges
              </h2>

              <p>
                Shipping charges, if applicable, will be displayed during
                checkout before the order is completed.
              </p>

              <p className="mt-3">
                Where free shipping is offered, the terms of the applicable
                promotional offer will apply.
              </p>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                5. Tracking
              </h2>

              <p>
                Once an order has been dispatched, customers may receive
                tracking information through email, SMS, WhatsApp or another
                communication channel provided by Legacy Decor or its logistics
                partner.
              </p>

              <p className="mt-3">
                Tracking information may take some time to become active after
                the shipment is handed over to the courier.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                6. Delayed Deliveries
              </h2>

              <p>
                Delivery estimates are indicative and are not guaranteed dates
                unless expressly stated otherwise.
              </p>

              <p className="mt-3">Delivery may be delayed because of:</p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Courier network disruptions</li>
                <li>Weather conditions</li>
                <li>Natural disasters</li>
                <li>Public holidays</li>
                <li>Government restrictions</li>
                <li>High-volume periods</li>
                <li>Incorrect or incomplete addresses</li>
                <li>Customer unavailability</li>
                <li>Remote-area delivery</li>
                <li>Security restrictions</li>
                <li>
                  Other circumstances outside Legacy Decor's reasonable control
                </li>
              </ul>

              <p className="mt-3">
                A delivery delay, by itself, does not constitute a valid reason
                for return or refund.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                7. Incorrect Address
              </h2>

              <p>
                Customers are responsible for providing a complete and accurate
                delivery address.
              </p>

              <p className="mt-3">
                If an order is returned to Legacy Decor because the customer
                provided an incorrect, incomplete or non-serviceable address,
                additional shipping charges may apply if re-dispatch is
                requested.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                8. Delivery Attempts
              </h2>

              <p>
                Our courier partners may make multiple delivery attempts in
                accordance with their operational policies.
              </p>

              <p className="mt-3">
                Customers are requested to remain available at the provided
                delivery address and respond to courier communications where
                necessary.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                9. Damaged Package at Delivery
              </h2>

              <p>
                If the package appears visibly damaged, crushed, torn, punctured
                or otherwise compromised at the time of delivery, customers are
                strongly advised to:
              </p>

              <ol className="list-decimal pl-6 mt-3 space-y-1">
                <li>Photograph the package before opening it</li>
                <li>Record an unboxing video where possible</li>
                <li>Photograph the shipping label</li>
                <li>Open the package carefully</li>
                <li>
                  Report any product damage to Legacy Decor within{" "}
                  <strong className="text-black">24 hours of delivery</strong>
                </li>
              </ol>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                10. Transportation Damage
              </h2>

              <p>
                Legacy Decor takes reasonable precautions to protect products
                during transportation.
              </p>

              <p className="mt-3">
                If the product is damaged during transportation, the customer
                must contact Legacy Decor within{" "}
                <strong className="text-black">24 hours of delivery</strong>{" "}
                with the required photographs/videos and packaging evidence.
              </p>

              <p className="mt-3">
                Please refer to our{" "}
                <strong className="text-black">Refund & Damage Policy</strong>{" "}
                for the complete claim procedure.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                11. Delivery Confirmation
              </h2>

              <p>
                A shipment may be considered delivered based on the delivery
                confirmation, electronic proof of delivery, OTP confirmation,
                signature, photograph or other confirmation provided by the
                logistics partner.
              </p>

              <p className="mt-3">
                If you believe an order has been incorrectly marked as
                delivered, please contact us immediately.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                12. Non-Delivery
              </h2>

              <p>
                If your order has not been delivered within the expected
                delivery period, please contact us with your order number.
              </p>

              <p className="mt-3">
                We will coordinate with the relevant logistics partner and
                provide an update.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                13. International Shipping
              </h2>

              <p>
                Unless specifically stated on the Website, Legacy Decor
                currently does not offer international shipping.
              </p>

              <p className="mt-3">
                Any future international shipping service will be subject to
                separate shipping terms, duties, taxes and applicable
                regulations.
              </p>
            </section>

            {/* 14 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                14. Contact
              </h2>

              <div className="space-y-2">
                <p>
                  <strong className="text-black">Legacy Decor</strong>
                </p>

                <p>
                  Website:{" "}
                  <a
                    href="https://www.legacydecor.in/"
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    www.legacydecor.in
                  </a>
                </p>

                <p>
                  Email:{" "}
                  <a
                    href="mailto:legacydecorvintage@gmail.com"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    legacydecorvintage@gmail.com
                  </a>
                </p>

                <p>
                  Phone/WhatsApp:{" "}
                  <a
                    href="tel:+919825081632"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    (+91) 9825081632
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
}
